import { forwardRef, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Clock } from 'lucide-react'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { Badge, Skeleton, SkeletonGroup } from '@/components/play'
import { formatEventStart } from '@/lib/explore'
import type { ExploreAudience, ExploreEvent } from '@/types/explore'
import { ExploreHosts } from './ExploreHosts'
import './explore.css'

/**
 * One listed event on /explore: a link to its public page named for the event, with its cover, name and start, which
 * the link reads as its description, and its hosts as cards below. Without a cover, or when it fails to load, it shows
 * the stacked play mark, never footage.
 * Happening now is the event's scheduled window, not evidence of live video, so it is a yellow badge with a clock:
 * never the live green, a dot or the word live.
 */
export const ExploreEventCard = forwardRef<HTMLAnchorElement, { event: ExploreEvent; audience: ExploreAudience }>(function ExploreEventCard({ event, audience }, ref) {
  const id = useId()
  const [nameId, nowId, startId] = ['name', 'now', 'start'].map((part) => `${id}-${part}`)
  const [brokenCover, setBrokenCover] = useState<string | null>(null)
  const cover = event.cover_url && event.cover_url !== brokenCover ? event.cover_url : null
  return (
    <article className="explore-event" aria-labelledby={nameId}>
      <Link
        ref={ref}
        to={event.path}
        className="explore-card"
        aria-labelledby={nameId}
        aria-describedby={[event.happening_now && nowId, startId].filter(Boolean).join(' ')}
      >
        <span className="explore-card__cover">
          {cover
            ? <img className="explore-card__image" src={cover} alt="" loading="lazy" onError={() => setBrokenCover(cover)} />
            : <span className="explore-card__fallback" aria-hidden="true"><PlayLogo markOnly /></span>}
          {event.happening_now && <Badge id={nowId} tone="accent" icon={<Clock />} className="explore-card__now">Happening now</Badge>}
        </span>
        <h3 id={nameId} className="explore-card__name">{event.name}</h3>
        <span id={startId} className="explore-card__start">
          <Calendar aria-hidden="true" />
          <time dateTime={event.starts_at}>{formatEventStart(event.starts_at)}</time>
        </span>
      </Link>
      <ExploreHosts hosts={event.hosts} audience={audience} eventPath={event.path} />
    </article>
  )
})

/** Cards in the shape of event cards while the first page loads, announced once. */
export function ExploreEventSkeletons({ count = 4 }: { count?: number }) {
  return (
    <SkeletonGroup label="Loading upcoming events">
      <div className="explore-grid">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="explore-skeleton">
            <Skeleton shape="media" />
            <Skeleton className="explore-skeleton__name" />
            <Skeleton className="explore-skeleton__line" />
            <div className="explore-skeleton__host">
              <Skeleton shape="circle" className="explore-skeleton__avatar" />
              <Skeleton className="explore-skeleton__line" />
            </div>
          </div>
        ))}
      </div>
    </SkeletonGroup>
  )
}
